import "./TasksDashboardPage.css";

import { OrganisationTypeConstants } from "../../../../constants";
import { AssetManagerTemplate, DeveloperTemplate } from "../../../../templates";
import { CompleteTaskModal, WorkflowTaskPanel } from "../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../useAuth";
import { Column, Row, TasksDashboard, UserHeader } from "../../../../widget";
import { NewTaskModal } from "../../components/NewTaskModal";
import { NewTaskObjectModal } from "./components";
import { useTasksDashboardContext } from "./TasksDashboardContext";

const TasksDashboardPage = (): JSX.Element => {
  const {
    tasks,
    lanes,
    fetchMoreLane,
    ganttGroups,
    onGroupExpand,
    onGroupShowMore,
    agendaTasks,
    hasMoreAgendaTasks,
    isLoadingAgendaTasks,
    onAgendaShowMore,
    ganttViewMode,
    onGanttViewModeChange,
    listGroups,
    onSectionExpand,
    onSectionShowMore,
    groupsHaveNextPage,
    fetchMoreGroups,
    isLoadingGroups,
    isLoading,
    variant,
    view,
    onViewChange,
    hideCompleted,
    onHideCompletedChange,
    hideUnassigned,
    onHideUnassignedChange,
    widgetFilters,
    onFilterChange,
    filterOptions,
    onTaskDrop,
    showCompleteTaskModal,
    completeTaskLabel,
    handleSubmitResolution,
    handleCancelComplete,
    onTaskClick,
    onObjectClick,
    onGroupObjectClick,
    onNewTask,
    onExportTable,
    isExporting,
    showNewTaskTypeModal,
    canCreateWorkflowTask,
    canCreateGeneralTask,
    isLoadingNewTaskTypes,
    handleNewTaskType,
    showNewTaskObjectModal,
    newTaskObjects,
    isLoadingNewTaskObjects,
    newTaskObjectsHaveError,
    handleNewTaskObjectSearch,
    handleNewTaskObject,
    handleCancelNewTask,
    taskPanelTask,
    taskPanelCurrentStepUuid,
    taskPanelWorkflow,
    taskPanelIsGeneralTask,
    taskPanelParentName,
    taskPanelParentObjectType,
    taskPanelParentObjectUuid,
    taskPanelPhaseOptions,
    taskPanelPhaseLabel,
    taskPanelIsRepeatablePhase,
    showTaskPanel,
    taskPanelRefresh,
    handleTaskPanelRefresh,
    handleCloseTaskPanel,
  } = useTasksDashboardContext();
  const { currentUserType } = useAuth();

  const isSeedPortal = currentUserType === OrganisationTypeConstants.ASSET_MANAGER;

  const content = (
    <>
      {/* The Seed shell already carries the profile menu, notifications and settings in
          SeedNavigation, so UserHeader would duplicate them for Asset Managers. */}
      {!isSeedPortal && (
        <Row spacingV="ml" className="TasksDashboardPage_headerRow">
          <Column span={12}>
            <UserHeader />
          </Column>
        </Row>
      )}
      <Row className="TasksDashboardPage_boardRow">
        <Column span={12} className="TasksDashboardPage_boardColumn">
          {/* The widget owns the shared header (view switcher, filters, toggles, New task) and
              renders the Kanban / Gantt / List view for the current `view`. The shell only holds
              the state and forwards the hook's data and handlers. */}
          <div className="TasksDashboardPage_board">
            <TasksDashboard
              variant={variant}
              tasks={tasks}
              // Column counts are the server's per-lane totals, and "Show more" pages that lane —
              // the cards on screen are one page, never the count (REQ-004).
              kanbanLanes={lanes}
              onLaneShowMore={fetchMoreLane}
              // Gantt rows come from the group query, so its counts are the server's rather than a
              // tally of the cards that happen to be loaded.
              ganttGroups={ganttGroups}
              onGroupExpand={onGroupExpand}
              onGroupShowMore={onGroupShowMore}
              // The Gantt's Agenda is one flat, deadline-ordered page of the same filtered set — its
              // own query, so switching to it costs one request rather than one per object.
              agendaTasks={agendaTasks}
              hasMoreAgendaTasks={hasMoreAgendaTasks}
              isLoadingAgendaTasks={isLoadingAgendaTasks}
              onAgendaShowMore={onAgendaShowMore}
              // Held by the page, so a filter change or a trip through another tab doesn't drop the
              // user's Timeline/Agenda choice.
              ganttViewMode={ganttViewMode}
              onGanttViewModeChange={onGanttViewModeChange}
              // The List reads the same groups, sliced per status: its counts are the server's and
              // each section pages on its own, so it isn't limited to the lanes' loaded pages.
              listGroups={listGroups}
              onSectionExpand={onSectionExpand}
              onSectionShowMore={onSectionShowMore}
              // The group list is paged too, so objects beyond the first page stay reachable.
              groupsHaveNextPage={groupsHaveNextPage}
              isLoadingGroups={isLoadingGroups}
              onGroupsShowMore={fetchMoreGroups}
              isLoading={isLoading}
              view={view}
              onViewChange={onViewChange}
              hideCompleted={hideCompleted}
              onHideCompletedChange={onHideCompletedChange}
              hideUnassigned={hideUnassigned}
              onHideUnassignedChange={onHideUnassignedChange}
              // Controlled filters: every selection re-queries, so the widget must not filter the
              // loaded cards itself or it would double-apply them.
              filters={widgetFilters}
              onFilterChange={onFilterChange}
              filterOptions={filterOptions}
              // The whole dropped block in one call, rather than `onTaskMove`'s card at a time: a
              // multi-select drop into Complete collects one Resolution for all of them.
              onTaskDrop={onTaskDrop}
              onTaskClick={onTaskClick}
              onObjectClick={onObjectClick}
              // A Gantt group is the object itself, so its link routes by the group's own type
              // (Project / Deal / Landholding) rather than through one of its tasks.
              onGroupObjectClick={onGroupObjectClick}
              onNewTask={onNewTask}
              onExportTable={onExportTable}
              isExporting={isExporting}
            />
          </div>
        </Column>
      </Row>
      {/* "New task" (REQ-014) in two steps. The dashboard is organisation-wide, so unlike the
          per-object task tabs it has no parent object to inherit: the type step reuses the same
          NewTaskModal those tabs use, then the chooser asks which object the task hangs off. */}
      <NewTaskModal
        show={showNewTaskTypeModal}
        canCreateWorkflowTask={canCreateWorkflowTask}
        canCreateGeneralTask={canCreateGeneralTask}
        isLoading={isLoadingNewTaskTypes}
        onCancel={handleCancelNewTask}
        onConfirm={handleNewTaskType}
      />
      <NewTaskObjectModal
        show={showNewTaskObjectModal}
        isGeneralTask={taskPanelIsGeneralTask}
        objects={newTaskObjects}
        isLoading={isLoadingNewTaskObjects}
        hasError={newTaskObjectsHaveError}
        // The parent universe is organisation-wide, so each term is searched server-side rather than
        // filtering a fixed page — `Autocomplete` debounces the typing before it gets here.
        onSearch={handleNewTaskObjectSearch}
        onCancel={handleCancelNewTask}
        onConfirm={handleNewTaskObject}
      />
      {/* Completing a task needs a Resolution (BR-02793), so a card dropped into the Complete lane is
          held here until this collects one — the same modal the task panel completes through. The
          dashboard owns the update rather than the modal, because a multi-select drop applies the one
          Resolution to every dropped card and the modal writes a single task. Cancelling sends
          nothing, which returns the cards to the lanes they came from.
          A single general task answers the submit with what its next instance would be cloned from,
          which is what keeps the modal open on its repeat step (REQ-008). */}
      <CompleteTaskModal
        show={showCompleteTaskModal}
        taskLabel={completeTaskLabel}
        onSubmitResolution={handleSubmitResolution}
        closeModal={handleCancelComplete}
        closePanel={handleCancelComplete}
        handleRefresh={handleTaskPanelRefresh}
      />
      {/* One task-detail surface for the whole dashboard: a task click in any view opens it on that
          task, and the New-task flow opens the same overlay with none, which is create mode. A saved
          change refreshes both the panel and the dashboard's queries — the group counts and section
          rows are server-side, so a status change is only reflected once they refetch.
          A general task has no workflow, so the panel's own `workflowObjectName` fallback is empty —
          the uniform parent name is passed so its parent row isn't suppressed. */}
      <WorkflowTaskPanel
        task={taskPanelTask}
        parentName={taskPanelParentName}
        currentStepUuid={taskPanelCurrentStepUuid}
        workflow={taskPanelWorkflow}
        isGeneralTask={taskPanelIsGeneralTask}
        // Lets a general task be created against a parent with no workflow to derive it from.
        parentObjectType={taskPanelParentObjectType}
        parentObjectUuid={taskPanelParentObjectUuid}
        // Read off the task's own workflow: a deal's stages are not the project phases the overlay
        // falls back to, and a phase with no matching steps leaves no step to create against.
        phaseOptions={taskPanelPhaseOptions}
        phaseLabel={taskPanelPhaseLabel}
        isRepeatablePhase={taskPanelIsRepeatablePhase}
        show={showTaskPanel}
        refresh={taskPanelRefresh}
        handleRefresh={handleTaskPanelRefresh}
        onClose={handleCloseTaskPanel}
      />
    </>
  );

  if (isSeedPortal) {
    return (
      <AssetManagerTemplate activeElement="Operations" fullHeight fullWidth>
        {content}
      </AssetManagerTemplate>
    );
  }

  return (
    // fullWidth: the six 312px Kanban lanes need more than the template's default 1600px cap.
    <DeveloperTemplate activeElement="Tasks" fullHeight fullWidth>
      {content}
    </DeveloperTemplate>
  );
};

export default TasksDashboardPage;
